import { create } from "zustand";
import { listMySites, listWarehouses, listMyOrgUnits, type SiteSummary, type Warehouse, type OrgUnit } from "../api/rest";

/** The dev/demo seed site — the only site allowed demo-data fallbacks (ADR 204). */
export const DEMO_SITE = import.meta.env.VITE_SITE_ID ?? "site-br-spo-01";
const FALLBACK_SITE = DEMO_SITE;

// ADR 201: the working context survives a refresh. Site is one global key;
// warehouse is one key PER site, so switching sites and back restores each
// site's own choice. Restore is validated against the freshly resolved lists
// (loadAvailableSites / loadAvailableWarehouses) — a stale id never sticks.
const LS_SITE = "twin.activeSiteId";
const lsWarehouseKey = (siteId: string) => `twin.activeWarehouseId.${siteId}`;

function lsGet(key: string): string | undefined {
  try { return window.localStorage.getItem(key) ?? undefined; } catch { return undefined; }
}
function lsSet(key: string, value: string | undefined) {
  // Swallow quota/private-mode errors — selection just won't stick.
  try {
    if (value === undefined) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* noop */ }
}

interface ParsedToken {
  // Site access no longer comes from the token (ADR 110) — it's resolved from grants and
  // read via GET /v1/me/sites. The token carries identity + roles only.
  roles?: string[];
  realm_access?: { roles?: string[] };
  [key: string]: unknown;
}

/** Site/global roles ranked low→high; unknown roles are ignored (floor -1 in roleRankOf). */
export const ROLE_RANK: Record<string, number> = {
  // ADR 409: sales is a read-level persona — the same rank as viewer, so a
  // sales-only account passes every `minRole: "viewer"` gate (Sessões, Kits...).
  viewer: 0, sales: 0, supervisor: 1, "site-admin": 2, "global-admin": 3,
};

/** Max rank over a role list; -1 when no role matches a known rank (incl. empty list). */
export function roleRankOf(roles: string[]): number {
  return roles.reduce((max, r) => Math.max(max, ROLE_RANK[r] ?? -1), -1);
}

export type Capability = "rtls" | "telemetry" | "inventory";

interface SiteState {
  activeSiteId: string;
  activeWarehouseId: string | undefined;
  isGlobalAdmin: boolean;
  /** Holds the `sales` realm role (ADR 409): every site, read-only, a three-item menu. */
  isSales: boolean;
  roles: string[];
  roleRank: number;
  availableSites: SiteSummary[];
  /** True once loadAvailableSites has finished, successfully or not (ADR 225).
   *  Distinguishes "still resolving" from "resolved to nothing", which the
   *  realtime gate needs: connecting before the answer arrives means opening a
   *  socket for the fallback site id and being closed for a tenant violation. */
  sitesResolved: boolean;
  /** The last site load FAILED (ADR 318) — distinct from "resolved to nothing".
   *  A failure must never be rendered as an empty system: that is how a 401 on an
   *  expired token turned into the first-run setup checklist. */
  sitesError: boolean;
  availableWarehouses: Warehouse[];
  /** True once loadAvailableWarehouses has finished for the active site, either way. */
  warehousesResolved: boolean;
  /** The last warehouse load FAILED (ADR 318). Same rule as sitesError: an
   *  unreachable/unauthorised backend is not "this site has no warehouse". */
  warehousesError: boolean;
  /** Org-units the user administers via grant (ADR 177) — drives grant-aware nav + the tenant picker. */
  adminOrgUnits: OrgUnit[];
  token: string | undefined;
  initFromToken: (parsed: ParsedToken, rawToken: string) => Promise<void>;
  /** Publish a re-issued bearer (ADR 318). Identity and grants are unchanged —
   *  a refresh only extends the same session — so only the raw token moves. */
  setToken: (rawToken: string) => void;
  setActiveSiteId: (id: string) => Promise<void>;
  setActiveWarehouseId: (id: string | undefined) => void;
  loadAvailableSites: () => Promise<void>;
  loadAvailableWarehouses: (siteId: string) => Promise<void>;
  loadAdminOrgUnits: () => Promise<void>;
  /** Capability flags (ADR 120) for a given site id, defaulting false when unresolved/absent. */
  capabilitiesFor: (siteId: string) => { rtls: boolean; telemetry: boolean; inventory: boolean };
  /** Convenience: capability flag for the currently active site. */
  hasCapability: (cap: Capability) => boolean;
}

/**
 * Mirror the active bearer token onto `window` so non-React API helpers
 * (rest.ts) can attach it without importing this store and creating a circular
 * dependency.
 */
function publishToken(t: string | undefined) {
  if (typeof window !== "undefined") {
    (window as any).__SITE_STORE_TOKEN = t;
  }
}

export const useSiteStore = create<SiteState>((set, get) => ({
  activeSiteId: lsGet(LS_SITE) ?? FALLBACK_SITE,
  activeWarehouseId: undefined,
  isGlobalAdmin: false,
  isSales: false,
  roles: [],
  roleRank: -1,
  availableSites: [],
  sitesResolved: false,
  sitesError: false,
  availableWarehouses: [],
  warehousesResolved: false,
  warehousesError: false,
  adminOrgUnits: [],
  token: undefined,

  async initFromToken(parsed, rawToken) {
    const roles   = parsed.realm_access?.roles ?? parsed.roles ?? [];
    const isAdmin = (roles as string[]).includes("global-admin");
    const isSales = (roles as string[]).includes("sales");
    set({ isGlobalAdmin: isAdmin, isSales, roles: roles as string[], roleRank: roleRankOf(roles as string[]), token: rawToken });
    publishToken(rawToken);
    // The active site comes from what the user can actually access (resolved grants via
    // /v1/me/sites), not a token claim — loadAvailableSites snaps activeSiteId into range.
    // ADR 358: sites must resolve FIRST (it picks activeSiteId), but org-units
    // (nav/tenant picker) and the active site's warehouses are independent of
    // each other — over the demo path's RTT each serial await was ~0,5 s of
    // blank boot, so they run in parallel.
    await get().loadAvailableSites();
    await Promise.all([
      get().loadAdminOrgUnits(),
      get().loadAvailableWarehouses(get().activeSiteId),
    ]);
  },

  setToken(rawToken) {
    set({ token: rawToken });
    publishToken(rawToken);
  },

  async setActiveSiteId(id) {
    lsSet(LS_SITE, id);
    set({
      activeSiteId: id, activeWarehouseId: undefined, availableWarehouses: [],
      // The new site's list is unknown until it arrives — not empty (ADR 318).
      warehousesResolved: false, warehousesError: false,
    });
    await get().loadAvailableWarehouses(id);
  },

  setActiveWarehouseId(id) {
    lsSet(lsWarehouseKey(get().activeSiteId), id);
    set({ activeWarehouseId: id });
  },

  async loadAvailableSites() {
    try {
      const list = await listMySites();
      set({ availableSites: list, sitesError: false });
      // The active site must be one the user can access; if the current selection isn't
      // in the resolved list (e.g. the default fallback, or access changed), snap to the
      // first accessible site.
      if (list.length === 0) {
        // Empty system (ADR 167) — genuinely nothing, so warehouses are resolved
        // too: nobody is waiting on a call that will never be made.
        set({ activeSiteId: "", activeWarehouseId: undefined, availableWarehouses: [],
              warehousesResolved: true, warehousesError: false });
      } else if (!list.some((s) => s.id === get().activeSiteId)) {
        // Snap via setActiveSiteId (not a raw set) so the new site's warehouses load too —
        // otherwise the setup checklist and /live keep reading the OLD site's (empty)
        // warehouse list after a site create/switch (ADR 175/183).
        await get().setActiveSiteId(list[0].id);
      }
    } catch {
      // Dev fallback: the backend may be unreachable. Flag it — a caller that
      // reads an empty availableSites must be able to tell "no access" from
      // "no answer" (ADR 318/225).
      set({ sitesError: true });
    } finally {
      // Resolved either way. On failure activeSiteId keeps the fallback, so the
      // realtime gate still attempts a connection and the badge can report the
      // outage — silence would be the wrong answer when the backend is down.
      set({ sitesResolved: true });
    }
  },

  async loadAvailableWarehouses(siteId) {
    if (!siteId) return;
    try {
      const list = await listWarehouses(siteId);
      // Persisted choice wins when it still exists (ADR 201); otherwise
      // auto-select if exactly one — most customers have a single warehouse.
      const persisted = lsGet(lsWarehouseKey(siteId));
      const restore =
        persisted && list.some((w) => w.id === persisted) ? persisted
        : list.length === 1 ? list[0].id : undefined;
      set({
        availableWarehouses: list, activeWarehouseId: restore,
        warehousesResolved: true, warehousesError: false,
      });
    } catch {
      // NOT "this site has no warehouse" (ADR 318). Before the flag existed, an
      // expired-token 401 here rendered /live's "Nenhum depósito ainda" and sent
      // `/` to the first-run setup checklist — a backend failure dressed up as an
      // unprovisioned system, and the reason a reload "fixed" it.
      set({ availableWarehouses: [], activeWarehouseId: undefined,
            warehousesResolved: true, warehousesError: true });
    }
  },

  async loadAdminOrgUnits() {
    try { set({ adminOrgUnits: await listMyOrgUnits() }); }
    catch { set({ adminOrgUnits: [] }); }
  },

  capabilitiesFor(siteId) {
    const s = get().availableSites.find((x) => x.id === siteId);
    return { rtls: !!s?.hasRtls, telemetry: !!s?.hasTelemetry, inventory: !!s?.hasInventory };
  },
  hasCapability(cap) {
    return get().capabilitiesFor(get().activeSiteId)[cap];
  },
}));
